import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import BrandLogo from '../components/BrandLogo';
import { ArrowRight, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const DEFAULT_AUTH_SETTINGS = {
  registration_enabled: true,
  forgot_password_enabled: true,
};

const postLoginRoute = (signedInUser) => (
  signedInUser?.show_welcome_after_login === false ? '/assessment' : '/welcome'
);

export default function Auth() {
  const [tab, setTab] = useState('login');
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState('');
  const [settings, setSettings] = useState(DEFAULT_AUTH_SETTINGS);

  const loadQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    setQuestionsError('');
    try {
      const { data } = await api.get('/auth/security-questions', { timeout: 10000 });
      if (!Array.isArray(data.questions) || !data.questions.length || data.questions.some((q) => typeof q !== 'string' || !q.trim())) {
        throw new Error('Security questions unavailable');
      }
      setQuestions(data.questions);
    } catch {
      setQuestions([]);
      setQuestionsError('Unable to load security questions. Please check that the service is running and retry.');
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  const tabs = [
    'login',
    ...(settings.registration_enabled ? ['register'] : []),
    ...(settings.forgot_password_enabled ? ['forgot'] : []),
  ];

  useEffect(() => {
    if (user) nav(postLoginRoute(user));
    loadQuestions();
    api.get('/auth/settings').then(r => setSettings({ ...DEFAULT_AUTH_SETTINGS, ...r.data })).catch(() => {});
  }, [user, nav, loadQuestions]);

  useEffect(() => {
    if (!tabs.includes(tab)) setTab('login');
  }, [tab, settings.registration_enabled, settings.forgot_password_enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-950 text-white p-12 flex-col justify-between relative overflow-hidden">
        <img src="https://images.pexels.com/photos/17485706/pexels-photo-17485706.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-950/80 to-[#002FA7]/40" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="auth-logo">
            <BrandLogo filled className="w-20 h-20" />
            <div>
              <div className="font-display font-bold text-lg">STArtIn AI</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">SMEs Thinking Artificial Intelligence</div>
            </div>
          </Link>
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-4">/ Assess · Refine · Deploy</div>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter leading-[1]">
            AI readiness,<br/>made measurable.
          </h1>
          <p className="text-zinc-300 mt-6 max-w-md">Sign in to save assessments, review past reports, and continue your AI transformation journey.</p>
        </div>
        <div className="relative text-xs text-zinc-500 font-mono">v1.0 · preview</div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <BrandLogo className="w-20 h-auto" />
              <span className="text-xs text-zinc-500 max-w-[160px]">SMEs Thinking Artificial Intelligence</span>
            </Link>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">/ Secure Access</div>
          <h2 className="font-display text-3xl font-bold text-zinc-950 tracking-tight">
            {tab === 'login' ? 'Welcome back.' : tab === 'register' ? 'Create account.' : 'Reset password.'}
          </h2>

          {/* Tabs */}
          <div className="flex gap-px bg-zinc-200 mt-8 mb-6" data-testid="auth-tabs">
            {tabs.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 text-xs uppercase tracking-wider py-3 transition-colors font-medium ${tab === t ? 'bg-zinc-950 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                data-testid={`auth-tab-${t}`}
              >
                {t === 'forgot' ? 'Forgot' : t}
              </button>
            ))}
          </div>

          {tab === 'login' && <LoginForm onSubmit={login} onSuccess={(signedInUser) => nav(postLoginRoute(signedInUser))} />}
          {tab === 'register' && <>
            {questionsError && <div role="alert" className="mb-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p>{questionsError}</p>
              <button type="button" onClick={loadQuestions} disabled={questionsLoading} className="mt-2 underline">Retry loading questions</button>
            </div>}
            <RegisterForm onSubmit={register} onSuccess={(signedInUser) => nav(postLoginRoute(signedInUser))} questions={questions} questionsLoading={questionsLoading} />
          </>}
          {tab === 'forgot' && <ForgotForm />}
        </div>
      </div>
    </div>
  );
}

function FieldError({ messages }) {
  if (!messages?.length) return null;
  return (
    <div className="mt-3 bg-red-50 border border-red-200 p-3 text-xs text-red-700 flex gap-2" data-testid="form-errors">
      <AlertCircle className="w-4 h-4 mt-px shrink-0" />
      <ul className="space-y-1">{messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
    </div>
  );
}

const inputCls = "w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all";
const labelCls = "text-xs font-medium text-zinc-700 mb-1.5 block uppercase tracking-wider";

function LoginForm({ onSubmit, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const submit = async (e) => {
    e.preventDefault();
    setErrors([]); setLoading(true);
    try { const signedInUser = await onSubmit(email, password); onSuccess(signedInUser); }
    catch (err) {
      const d = err?.response?.data?.detail;
      setErrors(Array.isArray(d) ? d : [d || 'Login failed']);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <div>
        <label className={labelCls}>Email</label>
        <input className={inputCls} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" required data-testid="login-email" />
      </div>
      <div>
        <label className={labelCls}>Password</label>
        <input className={inputCls} type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="login-password" />
      </div>
      <FieldError messages={errors} />
      <button type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] disabled:opacity-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium" data-testid="login-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

const emptyRegisterForm = (questions = []) => ({
  company_name: '',
  industry_segment: '',
  gst_number: '',
  email: '',
  password: '',
  confirm: '',
  security_question: questions[0] || '',
  security_answer: '',
});

const INDUSTRY_SEGMENTS = [
  'Agriculture & Agribusiness',
  'Automotive & Mobility',
  'Banking',
  'Construction & Real Estate',
  'Consumer Goods & Retail',
  'Education & Training',
  'Energy & Utilities',
  'Financial Services',
  'Healthcare & Life Sciences',
  'Hospitality & Tourism',
  'Information Technology & IT-enabled Services',
  'Logistics & Transportation',
  'Manufacturing',
  'Media & Entertainment',
  'Professional Services',
  'Telecommunications',
  'Other',
];

export function RegisterForm({ onSubmit, onSuccess, questions = [], questionsLoading = false, submitLabel = 'Create account', resetOnSuccess = false }) {
  const [form, setForm] = useState(() => emptyRegisterForm(questions));
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { if (questions.length && !form.security_question) setForm(f => ({ ...f, security_question: questions[0] })); }, [questions]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (!questions.includes(form.security_question)) { setErrors(['Please load and select a security question before registering.']); return; }
    if (form.password !== form.confirm) { setErrors(['Passwords do not match.']); return; }
    setLoading(true);
    try {
      const { confirm, ...payload } = form;
      const result = await onSubmit(payload);
      if (resetOnSuccess) setForm(emptyRegisterForm(questions));
      onSuccess?.(result);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErrors(Array.isArray(d) ? d : [d || 'Registration failed']);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="register-form">
      <div><label className={labelCls}>Company name</label><input className={inputCls} value={form.company_name} onChange={set('company_name')} required data-testid="reg-company" /></div>
      <div>
        <label className={labelCls}>Industry segment</label>
        <select className={inputCls} value={form.industry_segment} onChange={set('industry_segment')} required data-testid="reg-industry">
          <option value="" disabled>Select an industry segment</option>
          {INDUSTRY_SEGMENTS.map(segment => <option key={segment} value={segment}>{segment}</option>)}
        </select>
      </div>
      <div><label className={labelCls}>GST number</label><input className={inputCls} value={form.gst_number} onChange={set('gst_number')} placeholder="22AAAAA0000A1Z5" required data-testid="reg-gst" /></div>
      <div><label className={labelCls}>Email</label><input className={inputCls} type="email" value={form.email} onChange={set('email')} required data-testid="reg-email" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Password</label><input className={inputCls} type="password" value={form.password} onChange={set('password')} required data-testid="reg-pwd" /></div>
        <div><label className={labelCls}>Confirm</label><input className={inputCls} type="password" value={form.confirm} onChange={set('confirm')} required data-testid="reg-confirm" /></div>
      </div>
      <div>
        <label className={labelCls}>Security question</label>
        <select className={inputCls} value={form.security_question} onChange={set('security_question')} required disabled={!questions.length || questionsLoading} data-testid="reg-sec-q">
          {!questions.length && <option value="">{questionsLoading ? 'Loading security questions...' : 'Security questions unavailable'}</option>}
          {questions.map(q => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>
      <div><label className={labelCls}>Answer</label><input className={inputCls} value={form.security_answer} onChange={set('security_answer')} required data-testid="reg-sec-a" /></div>
      <FieldError messages={errors} />
      <button type="submit" disabled={loading || questionsLoading || !questions.length} className="w-full bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] disabled:opacity-50 transition-colors flex items-center justify-center gap-2 text-sm font-medium" data-testid="register-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{submitLabel} <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function ForgotForm() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const init = async (e) => {
    e.preventDefault();
    setErrors([]); setLoading(true);
    try { const r = await api.post('/auth/forgot-init', { email }); setQuestion(r.data.security_question); setStep(2); }
    catch (err) { setErrors([err?.response?.data?.detail || 'Email not found']); }
    finally { setLoading(false); }
  };
  const reset = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (newPwd !== confirm) { setErrors(['Passwords do not match']); return; }
    setLoading(true);
    try { await api.post('/auth/forgot-reset', { email, security_answer: answer, new_password: newPwd }); setDone(true); }
    catch (err) {
      const d = err?.response?.data?.detail;
      setErrors(Array.isArray(d) ? d : [d || 'Reset failed']);
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="bg-green-50 border border-green-200 p-5 text-sm text-green-800 flex items-start gap-3" data-testid="forgot-success">
      <CheckCircle2 className="w-5 h-5 mt-0.5" />
      <div>Password updated. You can now sign in with your new password.</div>
    </div>
  );

  return step === 1 ? (
    <form onSubmit={init} className="space-y-4" data-testid="forgot-step1">
      <div><label className={labelCls}>Registered email</label><input className={inputCls} type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="forgot-email" /></div>
      <FieldError messages={errors} />
      <button type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium" data-testid="forgot-init-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue'}
      </button>
    </form>
  ) : (
    <form onSubmit={reset} className="space-y-3" data-testid="forgot-step2">
      <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-3">Question: <strong>{question}</strong></div>
      <div><label className={labelCls}>Answer</label><input className={inputCls} value={answer} onChange={e => setAnswer(e.target.value)} required data-testid="forgot-answer" /></div>
      <div><label className={labelCls}>New password</label><input className={inputCls} type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required data-testid="forgot-new" /></div>
      <div><label className={labelCls}>Confirm</label><input className={inputCls} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="forgot-confirm" /></div>
      <FieldError messages={errors} />
      <button type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white px-6 py-3 hover:bg-[#00227A] disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium" data-testid="forgot-reset-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
      </button>
    </form>
  );
}
